/**
 * Multi-tab session coordination via BroadcastChannel.
 *
 * Ensures only one browser tab holds the decrypted mnemonic at a time.
 * When a new tab unlocks, existing tabs automatically lock (last-writer-wins).
 *
 * Gracefully degrades in environments where BroadcastChannel is unavailable
 * (SSR, older browsers, non-browser runtimes) by returning a no-op coordinator.
 *
 * @module tab-coordinator
 */

/** Channel name for session coordination messages. */
const CHANNEL_NAME = 'satsflow-wallet-session';

/** Message types sent between tabs. */
type MessageType = 'CLAIM_SESSION' | 'SESSION_RELEASED';

/** Message structure sent over BroadcastChannel. */
interface CoordinatorMessage {
  readonly type: MessageType;
  readonly tabId: string;
}

/**
 * Tab session coordinator interface.
 *
 * Manages cross-tab session state using BroadcastChannel.
 * Only one tab should hold the decrypted mnemonic at a time.
 */
export interface TabCoordinator {
  /** Announce this tab as the active session holder. Other tabs will lock. */
  claimSession(): void;

  /** Release the session (e.g., on tab close or manual lock). */
  releaseSession(): void;

  /** Register a callback invoked when another tab claims the session. */
  onSessionClaimed(callback: (claimingTabId: string) => void): void;

  /** Clean up the BroadcastChannel. Call on unmount or beforeunload. */
  destroy(): void;
}

/**
 * Create a no-op coordinator for environments without BroadcastChannel.
 *
 * @returns TabCoordinator that does nothing (safe fallback)
 */
function createNoOpCoordinator(): TabCoordinator {
  return {
    claimSession: () => {},
    releaseSession: () => {},
    onSessionClaimed: () => {},
    destroy: () => {},
  };
}

/**
 * Create a tab session coordinator.
 *
 * Uses BroadcastChannel to coordinate which browser tab holds the active
 * session. When a tab calls `claimSession()`, other tabs receive the
 * notification via `onSessionClaimed` and should lock themselves.
 *
 * If BroadcastChannel is not available, returns a no-op coordinator.
 *
 * @param tabId - Unique identifier for this tab (e.g., crypto.randomUUID())
 * @returns TabCoordinator instance
 */
export function createTabCoordinator(tabId: string): TabCoordinator {
  // Graceful degradation for environments without BroadcastChannel
  if (typeof BroadcastChannel === 'undefined') {
    return createNoOpCoordinator();
  }

  let channel: BroadcastChannel | null = new BroadcastChannel(CHANNEL_NAME);
  let sessionClaimedCallback: ((claimingTabId: string) => void) | null = null;

  const handleMessage = (event: MessageEvent<CoordinatorMessage>): void => {
    const message = event.data;

    // Ignore own messages — BroadcastChannel only delivers to OTHER tabs,
    // but we check anyway for safety.
    if (message.tabId === tabId) {
      return;
    }

    if (message.type === 'CLAIM_SESSION' && sessionClaimedCallback) {
      sessionClaimedCallback(message.tabId);
    }
  };

  channel.addEventListener('message', handleMessage);

  return {
    claimSession(): void {
      if (!channel) return;
      const message: CoordinatorMessage = { type: 'CLAIM_SESSION', tabId };
      channel.postMessage(message);
    },

    releaseSession(): void {
      if (!channel) return;
      const message: CoordinatorMessage = { type: 'SESSION_RELEASED', tabId };
      channel.postMessage(message);
    },

    onSessionClaimed(callback: (claimingTabId: string) => void): void {
      sessionClaimedCallback = callback;
    },

    destroy(): void {
      if (channel) {
        channel.removeEventListener('message', handleMessage);
        channel.close();
        channel = null;
      }
      sessionClaimedCallback = null;
    },
  };
}
